import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Snackbar
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Refresh, Add, Edit } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fetchCommissionTiers, createCommissionTier, updateCommissionTier } from '../../../services/marketing';
import CustomPagination from '../../../components/common/CustomPagination.js';

const CommissionTiers = () => {
  const { t } = useTranslation(['common']);
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    is_active: ''
  });
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 20,
    totalCount: 0
  });
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    tier_name: '',
    min_order_value: '',
    max_order_value: '',
    commission_percentage: '',
    priority: 1,
    is_active: true
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [saving, setSaving] = useState(false);

  const totalPages = Math.ceil(pagination.totalCount / pagination.rowsPerPage) || 1;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        ...filters,
        page: pagination.page + 1,
        page_size: pagination.rowsPerPage
      };
      
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await fetchCommissionTiers(params);
      
      if (response.results) {
        setTiers(response.results);
        setPagination(prev => ({
          ...prev,
          totalCount: response.count || 0
        }));
      } else {
        setTiers(Array.isArray(response) ? response : []);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to fetch commission tiers');
      setTiers([]);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.rowsPerPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setPagination(prev => ({ ...prev, rowsPerPage: newRowsPerPage, page: 0 }));
  };

  const handleCustomRowsInputChange = (value) => {
    setCustomRowsInput(value);
  };

  const handleCustomRowsInputBlur = () => {
    const value = parseInt(customRowsInput);
    if (value && value > 0 && value <= 1000) {
      setPagination(prev => ({ ...prev, rowsPerPage: value, page: 0 }));
      setCustomRowsActive(false);
      setCustomRowsInput('');
    } else {
      setCustomRowsInput('');
      setCustomRowsActive(false);
    }
  };

  const handleOpenModal = (tier = null) => {
    if (tier) {
      setEditId(tier.id);
      setForm({
        tier_name: tier.tier_name || '',
        min_order_value: tier.min_order_value || '',
        max_order_value: tier.max_order_value || '',
        commission_percentage: tier.commission_percentage || '',
        priority: tier.priority || 1,
        is_active: tier.is_active !== undefined ? tier.is_active : true
      });
    } else {
      setEditId(null);
      setForm({
        tier_name: '',
        min_order_value: '',
        max_order_value: '',
        commission_percentage: '',
        priority: 1,
        is_active: true
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditId(null);
    setForm({
      tier_name: '',
      min_order_value: '',
      max_order_value: '',
      commission_percentage: '',
      priority: 1,
      is_active: true
    });
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    // Validation
    if (!form.tier_name || !form.min_order_value || !form.commission_percentage) {
      setSnackbar({ open: true, message: 'Please fill in all required fields', severity: 'error' });
      return;
    }

    if (parseFloat(form.min_order_value) < 0) {
      setSnackbar({ open: true, message: 'Min order value must be 0 or greater', severity: 'error' });
      return;
    }

    if (form.max_order_value && parseFloat(form.max_order_value) <= parseFloat(form.min_order_value)) {
      setSnackbar({ open: true, message: 'Max order value must be greater than min order value', severity: 'error' });
      return;
    }

    if (parseFloat(form.commission_percentage) < 0 || parseFloat(form.commission_percentage) > 100) {
      setSnackbar({ open: true, message: 'Commission percentage must be between 0 and 100', severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        tier_name: form.tier_name,
        min_order_value: parseFloat(form.min_order_value),
        max_order_value: form.max_order_value ? parseFloat(form.max_order_value) : null,
        commission_percentage: parseFloat(form.commission_percentage),
        priority: parseInt(form.priority) || 1,
        is_active: form.is_active
      };

      if (editId) {
        await updateCommissionTier(editId, payload);
        setSnackbar({ open: true, message: 'Commission tier updated successfully', severity: 'success' });
      } else {
        await createCommissionTier(payload);
        setSnackbar({ open: true, message: 'Commission tier created successfully', severity: 'success' });
      }
      
      handleCloseModal();
      fetchData();
    } catch (err) {
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.detail || err.message || 'Failed to save commission tier', 
        severity: 'error' 
      });
    } finally {
      setSaving(false);
    }
  };

  const rowsWithSerial = useMemo(() => {
    return tiers.map((tier, index) => ({
      ...tier,
      sn: pagination.page * pagination.rowsPerPage + index + 1
    }));
  }, [tiers, pagination.page, pagination.rowsPerPage]);

  const columns = useMemo(() => [
    {
      field: 'sn',
      headerName: 'S/N',
      width: 80,
      sortable: false,
      filterable: false
    },
    {
      field: 'tier_name',
      headerName: 'Tier Name',
      flex: 1,
      minWidth: 150,
      sortable: false
    },
    {
      field: 'min_order_value',
      headerName: 'Min Order Value (Naira)',
      flex: 1,
      minWidth: 180,
      sortable: false,
      renderCell: (params) => {
        const value = params.value || 0;
        return `₦${parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
    },
    {
      field: 'max_order_value',
      headerName: 'Max Order Value (Naira)',
      flex: 1,
      minWidth: 180,
      sortable: false,
      renderCell: (params) => {
        if (!params.value) return 'Unlimited';
        const value = params.value;
        return `₦${parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
    },
    {
      field: 'commission_percentage',
      headerName: 'Commission %',
      width: 150,
      sortable: false,
      renderCell: (params) => {
        const value = params.value || 0;
        return `${parseFloat(value).toFixed(2)}%`;
      }
    },
    {
      field: 'priority',
      headerName: 'Priority',
      width: 100,
      sortable: false
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 120,
      sortable: false,
      renderCell: (params) => {
        return (
          <Chip
            label={params.value ? 'Active' : 'Inactive'}
            color={params.value ? 'success' : 'default'}
            size="small"
          />
        );
      }
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => {
        if (!params.value) return 'N/A';
        try {
          return format(new Date(params.value), 'MMM dd, yyyy');
        } catch {
          return 'N/A';
        }
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <Button
            size="small"
            startIcon={<Edit />}
            onClick={() => handleOpenModal(params.row)}
            sx={{ color: '#DC143C' }}
          >
            Edit
          </Button>
        );
      }
    }
  ], []);

  if (loading && tiers.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C', mb: 2 }}>
          Commission Tiers
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchData}
            disabled={loading}
          >
            {t('buttons.refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenModal()}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
          >
            Add Tier
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                label="Search"
                fullWidth
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Search by tier name..."
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                select
                label="Status"
                fullWidth
                value={filters.is_active}
                onChange={(e) => handleFilterChange('is_active', e.target.value)}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
              >
                <option value="">All</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ 
            width: '100%',
            overflowX: 'auto',
            minHeight: 520
          }}>
            <DataGrid
              rows={rowsWithSerial}
              columns={columns}
              disableSelectionOnClick
              loading={loading}
              hideFooterPagination
              hideFooter
              getRowSpacing={() => ({ top: 2, bottom: 2 })}
              sx={{
                border: 'none',
                minWidth: 1200,
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: theme.palette.primary.main,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1rem'
                },
                '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                  color: 'white'
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#FFF0F3'
                },
                '& .MuiDataGrid-cell': {
                  fontSize: '1rem'
                },
                '& .MuiDataGrid-row': {
                  marginBottom: '4px',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ mt: 3 }}>
        <CustomPagination
          page={pagination.page}
          totalPages={totalPages}
          rowsPerPage={pagination.rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          customRowsActive={customRowsActive}
          customRowsInput={customRowsInput}
          onCustomRowsInputChange={handleCustomRowsInputChange}
          onCustomRowsInputBlur={handleCustomRowsInputBlur}
        />
      </Box>

      {/* Add/Edit Tier Modal */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editId ? 'Edit Commission Tier' : 'Add Commission Tier'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Tier Name"
                  fullWidth
                  required
                  value={form.tier_name}
                  onChange={(e) => handleFormChange('tier_name', e.target.value)}
                  placeholder="e.g., Bronze, Silver, Gold"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Min Order Value (Naira)"
                  fullWidth
                  required
                  type="number"
                  value={form.min_order_value}
                  onChange={(e) => handleFormChange('min_order_value', e.target.value)}
                  inputProps={{ min: 0, step: 0.01 }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Max Order Value (Naira)"
                  fullWidth
                  type="number"
                  value={form.max_order_value}
                  onChange={(e) => handleFormChange('max_order_value', e.target.value)}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText="Leave empty for unlimited"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Commission Percentage"
                  fullWidth
                  required
                  type="number"
                  value={form.commission_percentage}
                  onChange={(e) => handleFormChange('commission_percentage', e.target.value)}
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                  helperText="0-100%"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Priority"
                  fullWidth
                  type="number"
                  value={form.priority}
                  onChange={(e) => handleFormChange('priority', e.target.value)}
                  inputProps={{ min: 1 }}
                  helperText="Lower number = higher priority"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.is_active}
                      onChange={(e) => handleFormChange('is_active', e.target.checked)}
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} disabled={saving}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            disabled={saving}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
          >
            {saving ? <CircularProgress size={20} /> : (editId ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CommissionTiers;
